import { useCallback, useEffect, useRef, useState } from "react";
import { useLang } from "../i18n";
import api from "../lib/api";
import Header from "../components/Header";
import MetricsGrid from "../components/MetricsGrid";
import TradeCard from "../components/TradeCard";
import PositionsPanel from "../components/PositionsPanel";
import PerformancePanel from "../components/PerformancePanel";
import JournalPanel from "../components/JournalPanel";
import NewsPanel from "../components/NewsPanel";
import SettingsPanel from "../components/SettingsPanel";
import BacktestPanel from "../components/BacktestPanel";
import { RefreshCw, LayoutDashboard, LineChart, BookOpen, Settings2, FlaskConical, ShieldAlert } from "lucide-react";

export default function Dashboard() {
  const { t } = useLang();
  const [account, setAccount] = useState(null);
  const [signals, setSignals] = useState([]);
  const [regime, setRegime] = useState(null);
  const [tab, setTab] = useState("overview");
  const [refreshKey, setRefreshKey] = useState(0);
  const [spinning, setSpinning] = useState(false);

  const loadCore = useCallback(async () => {
    const [a, r] = await Promise.all([api.get("/account"), api.get("/market/regime")]);
    setAccount(a.data); setRegime(r.data);
  }, []);
  const loadSignals = useCallback(async () => {
    const s = await api.get("/signals?min_confidence=55");
    setSignals(s.data.signals);
  }, []);

  const refresh = useCallback(async () => {
    setSpinning(true);
    try { await Promise.all([loadCore(), loadSignals()]); setRefreshKey((k) => k + 1); }
    finally { setTimeout(() => setSpinning(false), 400); }
  }, [loadCore, loadSignals]);

  useEffect(() => { refresh(); }, [refresh]);
  const timer = useRef();
  useEffect(() => {
    timer.current = setInterval(() => { loadCore(); setRefreshKey((k) => k + 1); }, 20000);
    return () => clearInterval(timer.current);
  }, [loadCore]);

  const onAccountUpdate = (data) => { if (data) setAccount(data); else loadCore(); };

  const TABS = [
    { key: "overview", label: t("overview"), icon: LayoutDashboard },
    { key: "performance", label: t("performance"), icon: LineChart },
    { key: "backtest", label: t("backtest"), icon: FlaskConical },
    { key: "journal", label: t("journal"), icon: BookOpen },
    { key: "settings", label: t("settings"), icon: Settings2 },
  ];

  return (
    <div className="min-h-screen bg-[#05050A] text-white">
      <Header account={account} onUpdate={onAccountUpdate} />

      {/* disclaimer strip */}
      <div className="bg-[#0F1015] border-b border-[#1E2028] px-4 lg:px-6 py-1.5 text-[10px] text-[#6B7280] font-mono text-center">
        {t("disclaimer")}
      </div>

      {/* sub nav */}
      <div className="sticky top-16 z-30 bg-[#05050A] border-b border-[#1E2028] px-4 lg:px-6 flex items-center justify-between overflow-x-auto">
        <div className="flex items-center">
          {TABS.map((tb) => (
            <button key={tb.key} data-testid={`nav-${tb.key}`} onClick={() => setTab(tb.key)}
              className={`flex items-center gap-2 px-3 lg:px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors duration-200 ${
                tab === tb.key ? "border-[#FACC15] text-[#FACC15]" : "border-transparent text-[#9CA3AF] hover:text-white"}`}>
              <tb.icon className="w-4 h-4" /> {tb.label}
            </button>
          ))}
        </div>
        <button data-testid="refresh-btn" onClick={refresh}
          className="flex items-center gap-2 text-xs font-mono text-[#9CA3AF] hover:text-white transition-colors duration-200 px-2">
          <RefreshCw className={`w-3.5 h-3.5 ${spinning ? "animate-spin" : ""}`} /> {t("refresh")}
        </button>
      </div>

      <main className="px-4 lg:px-6 py-5 max-w-[1600px] mx-auto space-y-5">
        {account?.ai_paused && (
          <div data-testid="ai-paused-banner" className="flex items-center gap-3 border border-[#EF4444]/50 bg-[#EF4444]/10 px-4 py-2.5">
            <ShieldAlert className="w-4 h-4 text-[#EF4444] shrink-0" />
            <span className="text-sm font-display font-bold text-[#EF4444]">{t("aiPausedTitle")}</span>
            <span className="text-xs text-[#9CA3AF]">· {account.ai_pause_reason}</span>
          </div>
        )}
        {account?.mode === "AUTO" && !account?.kill_switch && !account?.ai_paused && (
          <div data-testid="auto-active-banner" className="flex items-center gap-3 border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 px-4 py-2.5">
            <span className="live-dot inline-block w-2.5 h-2.5 rounded-full bg-[#8B5CF6]" />
            <span className="text-sm font-display font-bold text-[#8B5CF6]">{t("autoActive")}</span>
            <span className="text-xs text-[#9CA3AF] hidden sm:inline">· {t("autoDesc")}</span>
            <span className="ml-auto text-[10px] font-mono text-[#8B5CF6] uppercase tracking-wider">{t("live247")}</span>
          </div>
        )}

        {tab === "overview" && (
          <>
            <MetricsGrid account={account} regime={regime} />
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
              <section className="xl:col-span-5 order-1">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-display text-lg font-bold flex items-center gap-2">
                    <span className="live-dot inline-block w-2 h-2 rounded-full bg-[#FACC15]" /> {t("aiSignals")}
                  </h2>
                  <span className="text-xs font-mono text-[#6B7280]">{signals.length} · {t("marketRegime")}: {regime?.dominant_regime}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-3 max-h-[720px] overflow-y-auto pr-1">
                  {signals.map((s, i) => (
                    <TradeCard key={s.id} signal={s} index={i} onExecuted={refresh} />
                  ))}
                  {signals.length === 0 && <div className="text-sm text-[#6B7280] font-mono py-8 text-center border border-[#1E2028]">—</div>}
                </div>
              </section>
              <section className="xl:col-span-7 order-2 space-y-5">
                <PositionsPanel refreshKey={refreshKey} onChange={loadCore} />
                <NewsPanel />
              </section>
            </div>
          </>
        )}

        {tab === "performance" && <PerformancePanel refreshKey={refreshKey} />}
        {tab === "backtest" && <BacktestPanel />}
        {tab === "journal" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <JournalPanel refreshKey={refreshKey} />
            <NewsPanel />
          </div>
        )}
        {tab === "settings" && <SettingsPanel account={account} onUpdate={onAccountUpdate} />}
      </main>
    </div>
  );
}
